import type { LucideIcon } from 'lucide-react'
import {
  Calendar,
  FileText,
  Globe,
  Heart,
  Mail,
  MessageSquare,
  Monitor,
  ShieldCheck,
  Sparkles,
  Terminal,
  TrendingUp,
  Twitter,
  Wallet,
} from 'lucide-react'
import { CMS_DEMO_ASSET_URLS } from './demo-assets'
import type { CmsLandingCarouselPageKey, LandingCarouselSlideDto } from './types'

/** Curated Lucide names available in the admin icon picker. */
export const LANDING_CAROUSEL_ICON_NAMES = [
  'Mail',
  'Calendar',
  'Twitter',
  'Heart',
  'TrendingUp',
  'Wallet',
  'MessageSquare',
  'FileText',
  'Monitor',
  'Terminal',
  'Globe',
  'ShieldCheck',
  'Sparkles',
] as const

export type LandingCarouselIconName = (typeof LANDING_CAROUSEL_ICON_NAMES)[number]

const ICON_MAP: Record<LandingCarouselIconName, LucideIcon> = {
  Mail,
  Calendar,
  Twitter,
  Heart,
  TrendingUp,
  Wallet,
  MessageSquare,
  FileText,
  Monitor,
  Terminal,
  Globe,
  ShieldCheck,
  Sparkles,
}

export type LandingCarouselViewSlide = {
  id: string
  step: string
  title: string
  description: string
  color: string
  demoSrc: string
  posterSrc: string
  iconName: string | null
  iconUrl: string | null
  icon: LucideIcon
}

/** baby: iconUrl wins; otherwise resolve Lucide by name; else Sparkles. */
export function resolveLandingCarouselIcon(
  iconName: string | null | undefined,
  iconUrl?: string | null,
): { icon: LucideIcon; iconUrl: string | null } {
  if (iconUrl) {
    return { icon: Sparkles, iconUrl }
  }
  if (iconName && iconName in ICON_MAP) {
    return { icon: ICON_MAP[iconName as LandingCarouselIconName], iconUrl: null }
  }
  return { icon: Sparkles, iconUrl: null }
}

export function mapLandingCarouselSlideDto(slide: LandingCarouselSlideDto): LandingCarouselViewSlide {
  const resolved = resolveLandingCarouselIcon(slide.iconName, slide.iconUrl)
  return {
    id: slide.id,
    step: slide.stepLabel || 'Demo',
    title: slide.title,
    description: slide.description,
    color: slide.accentColor || '#21728d',
    demoSrc: slide.demoUrl,
    posterSrc: slide.posterUrl || CMS_DEMO_ASSET_URLS.healthPoster,
    iconName: slide.iconName,
    iconUrl: resolved.iconUrl,
    icon: resolved.icon,
  }
}

const NINXL_FALLBACK_SLIDES: LandingCarouselViewSlide[] = [
  {
    id: 'fallback-ninxl-gmail',
    step: 'Apps',
    title: 'Gmail',
    description:
      'Search, organize, and respond to emails with simple commands. Genie reads your inbox, filters important messages, and drafts replies.',
    icon: Mail,
    iconName: 'Mail',
    iconUrl: null,
    color: '#EA4335',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-calendar',
    step: 'Apps',
    title: 'Calendar',
    description:
      'Check your schedule, create events, and get reminders. Just ask what is on your calendar today and Genie shows your day at a glance.',
    icon: Calendar,
    iconName: 'Calendar',
    iconUrl: null,
    color: '#4285F4',
    demoSrc: CMS_DEMO_ASSET_URLS.calendar,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-twitter',
    step: 'Apps',
    title: 'Twitter / X',
    description:
      'Draft tweets, schedule posts, and manage your social presence. Genie helps you craft engaging content and post at optimal times.',
    icon: Twitter,
    iconName: 'Twitter',
    iconUrl: null,
    color: '#1DA1F2',
    demoSrc: CMS_DEMO_ASSET_URLS.twitterX,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-notion',
    step: 'Apps',
    title: 'Notion',
    description:
      'Search, summarize, and organize your documents. Genie can find that important file and extract key information for you.',
    icon: FileText,
    iconName: 'FileText',
    iconUrl: null,
    color: '#7C3AED',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-groww',
    step: 'Apps',
    title: 'Groww & Brokers',
    description:
      'View positions, check watchlists, and get market insights. Genie brings your portfolio data right to the chat.',
    icon: TrendingUp,
    iconName: 'TrendingUp',
    iconUrl: null,
    color: '#387ED1',
    demoSrc: CMS_DEMO_ASSET_URLS.groww,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-binance',
    step: 'Apps',
    title: 'Binance',
    description:
      'Check crypto balances, view market data, and monitor your Binance portfolio. Genie shows your holdings in real time.',
    icon: Wallet,
    iconName: 'Wallet',
    iconUrl: null,
    color: '#F0B90B',
    demoSrc: CMS_DEMO_ASSET_URLS.groww,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-contacts',
    step: 'Apps',
    title: 'Contacts',
    description:
      'Search your contacts, share contact info, and manage your address book with simple commands. Genie finds who you need instantly.',
    icon: MessageSquare,
    iconName: 'MessageSquare',
    iconUrl: null,
    color: '#25D366',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-system-control',
    step: 'Desktop',
    title: 'System Control',
    description: 'Open apps, manage files, and control your desktop. Tell Genie what you need and it handles the rest.',
    icon: Monitor,
    iconName: 'Monitor',
    iconUrl: null,
    color: '#3b82f6',
    demoSrc: CMS_DEMO_ASSET_URLS.systemControl,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-local-shell',
    step: 'Desktop',
    title: 'Local Shell',
    description:
      'Run commands on your computer from the chat. Search files, open folders, and automate tasks through natural language.',
    icon: Terminal,
    iconName: 'Terminal',
    iconUrl: null,
    color: '#10b981',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-connect',
    step: 'Desktop',
    title: 'Connect to App',
    description:
      'One account across web, mobile, and desktop. Start a task on your phone, let your desktop finish it. Seamlessly in sync.',
    icon: Globe,
    iconName: 'Globe',
    iconUrl: null,
    color: '#8b5cf6',
    demoSrc: CMS_DEMO_ASSET_URLS.health,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ninxl-remote',
    step: 'Desktop',
    title: 'Remote Control',
    description:
      'Control your desktop from anywhere. Connect securely and run tasks on your home or office PC with no port forwarding needed.',
    icon: ShieldCheck,
    iconName: 'ShieldCheck',
    iconUrl: null,
    color: '#6366f1',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
]

const NINGENIE_HOME_FALLBACK_SLIDES: LandingCarouselViewSlide[] = [
  {
    id: 'fallback-ningenie-gmail',
    step: 'Step 1',
    title: 'How to manage Gmail?',
    description:
      'Search, organize, and respond to emails with simple voice commands. Genie reads your inbox, filters important messages, and drafts replies.',
    icon: Mail,
    iconName: 'Mail',
    iconUrl: null,
    color: '#EA4335',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ningenie-calendar',
    step: 'Step 2',
    title: 'How to use Calendar?',
    description:
      'Check your schedule, create events, and get reminders. Just say "What\'s on my calendar today?" and Genie shows your day at a glance.',
    icon: Calendar,
    iconName: 'Calendar',
    iconUrl: null,
    color: '#4285F4',
    demoSrc: CMS_DEMO_ASSET_URLS.calendar,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ningenie-twitter',
    step: 'Step 3',
    title: 'How to post on Twitter/X?',
    description:
      'Draft tweets, schedule posts, and manage your social presence. Genie helps you craft engaging content and post at optimal times.',
    icon: Twitter,
    iconName: 'Twitter',
    iconUrl: null,
    color: '#1DA1F2',
    demoSrc: CMS_DEMO_ASSET_URLS.twitterX,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ningenie-health',
    step: 'Step 4',
    title: 'How to check Health?',
    description:
      'Track your symptoms, set health reminders, and get personalized insights. Genie integrates with your health apps to keep you informed.',
    icon: Heart,
    iconName: 'Heart',
    iconUrl: null,
    color: '#FF6B6B',
    demoSrc: CMS_DEMO_ASSET_URLS.health,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ningenie-groww',
    step: 'Step 5',
    title: 'How to schedule cancellation of a open position on Groww/Zerodha?',
    description:
      'View positions, check watchlists, and get market insights from your Zerodha account. Genie brings your portfolio data right to the chat.',
    icon: TrendingUp,
    iconName: 'TrendingUp',
    iconUrl: null,
    color: '#387ED1',
    demoSrc: CMS_DEMO_ASSET_URLS.groww,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ningenie-binance',
    step: 'Step 6',
    title: 'How to use Binance?',
    description:
      'Check your crypto balances, view market data, and monitor your Binance portfolio. Genie shows your holdings in real-time.',
    icon: Wallet,
    iconName: 'Wallet',
    iconUrl: null,
    color: '#F0B90B',
    demoSrc: CMS_DEMO_ASSET_URLS.groww,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ningenie-contacts',
    step: 'Step 7',
    title: 'How to use Contacts?',
    description:
      'Search your contacts, share contact info, and manage your address book with simple commands. Genie finds who you need instantly.',
    icon: MessageSquare,
    iconName: 'MessageSquare',
    iconUrl: null,
    color: '#25D366',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
  {
    id: 'fallback-ningenie-documents',
    step: 'Step 8',
    title: 'How to manage Documents?',
    description:
      'Search, summarize, and organize your documents. Genie can find that important file and extract key information for you.',
    icon: FileText,
    iconName: 'FileText',
    iconUrl: null,
    color: '#7C3AED',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster,
  },
]

export function getLandingCarouselFallbackSlides(
  pageKey: CmsLandingCarouselPageKey,
): LandingCarouselViewSlide[] {
  return pageKey === 'ninxl' ? NINXL_FALLBACK_SLIDES : NINGENIE_HOME_FALLBACK_SLIDES
}

export async function fetchLandingCarouselSlides(
  pageKey: CmsLandingCarouselPageKey,
): Promise<LandingCarouselViewSlide[]> {
  try {
    const response = await fetch(`/api/cms/landing-carousel?page=${pageKey}`, {
      cache: 'no-store',
    })
    if (!response.ok) return getLandingCarouselFallbackSlides(pageKey)
    const payload = (await response.json()) as { slides?: LandingCarouselSlideDto[] }
    if (!payload.slides?.length) return getLandingCarouselFallbackSlides(pageKey)
    return payload.slides.map(mapLandingCarouselSlideDto)
  } catch {
    return getLandingCarouselFallbackSlides(pageKey)
  }
}
